// Flow priority (ENG-1616): `rightCta.flowPriority` lists widget flow IDs, highest priority first.
// When it is absent, widgets keep their stored order (the order they were created in), which is
// how the tag has always decided which of two competing widgets wins. Flows missing from a saved
// list (new ones, or ones created by other tools) follow every listed flow in that stored order.

/** Whether the project has saved its own flow order. */
const hasFlowPriority = (flowPriority) => Array.isArray(flowPriority) && flowPriority.length > 0

/**
 * The widgets in the order the tag starts them: by their flow's place in `flowPriority`, keeping the
 * stored order for widgets of the same or unlisted flows. Returns the same array when nothing is saved.
 */
export function orderWidgetsByFlowPriority(widgets, flowPriority) {
  if (!hasFlowPriority(flowPriority) || !Array.isArray(widgets)) {
    return widgets
  }
  const rank = new Map()
  flowPriority.forEach((id, index) => {
    if (!rank.has(id)) {
      rank.set(id, index)
    }
  })
  const rankOf = (widget) => rank.get(widget?.contentId) ?? Number.POSITIVE_INFINITY
  return widgets
    .map((widget, index) => ({ widget, index }))
    .sort((a, b) => rankOf(a.widget) - rankOf(b.widget) || a.index - b.index)
    .map(({ widget }) => widget)
}

/**
 * Widget flow IDs from highest to lowest priority, as the tag ranks them: a saved list first,
 * then the rest in the order of their first widget, then flows with no widget in stored order.
 */
export function flowPriorityOrder(flows, widgets, flowPriority) {
  const ids = (flows || []).filter((flow) => flow && flow.isSystemFlow !== true).map((flow) => flow.id)
  const known = new Set(ids)
  const order = []
  const seen = new Set()
  const add = (id) => {
    if (known.has(id) && !seen.has(id)) {
      seen.add(id)
      order.push(id)
    }
  }
  if (hasFlowPriority(flowPriority)) {
    flowPriority.forEach(add)
  }
  ;(widgets || []).forEach((widget) => add(widget?.contentId))
  ids.forEach(add)
  return order
}
